import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Paper,
  Button,
  Grid,
  Fade,
  Zoom
} from '@mui/material';
import {
  Check,
  ArrowBack,
} from '@mui/icons-material';
import LoadingImage from '../components/LoadingImage';

const YedekParca: React.FC = () => {
  // SEO Meta Tags
  useEffect(() => {
    document.title = "Tır ve Frigo Dorse Yedek Parçaları - Kale Treyler | Mersin";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Mersin'de tır ve frigo dorse yedek parça satışı. Dorse çamurlukları, kapı kilitleri, profil parçaları, izolasyon malzemeleri ve hırdavat çeşitleri.");
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', "dorse yedek parça, tır yedek parça, frigo dorse malzemeleri, Mersin yedek parça, dorse kilit sistemleri");
    }
    
    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Tır ve Frigo Dorse Yedek Parçaları - Kale Treyler | Mersin");
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Mersin'de tır ve frigo dorse yedek parça satışı. Dorse çamurlukları, kapı kilitleri, profil parçaları, izolasyon malzemeleri ve hırdavat çeşitleri.");
    }

    // Update Canonical URL
    const canonicalLink = (document.querySelector('link[rel="canonical"]') || document.createElement('link')) as HTMLLinkElement;
    canonicalLink.setAttribute('rel', 'canonical');
    canonicalLink.setAttribute('href', 'https://kaletreyler.com/hizmetler/yedek-parca');
    if (!document.head.contains(canonicalLink)) {
      document.head.appendChild(canonicalLink);
    }
  }, []);

  const productGroups = [
    {
      title: 'Tır Yedek Parçaları',
      description: 'Tır araçlarınız için geniş yedek parça yelpazesi. Motor, fren, süspansiyon ve elektrik sistemleri için kaliteli parçalar.',
      benefits: ['Rüzgarlık ve Etek parçaları', 'Koruma malzemeleri', 'Çamurluklar', 'Elektrik sistemi', 'Stop ve Stop Camları'],
      image: '/43.png'
    },
    {
      title: 'Dorse Yedek Parçaları',
      description: 'Frigo dorse kasaları için özel tasarlanmış yedek parçalar. Soğutma, yalıtım ve mekanik sistemler için çözümler.',
      benefits: ['Yalıtım malzemeleri', 'Kapı sistemleri', 'Kontrol panelleri', 'Elektrik Aktarım Kabloları', 'Mekanik aksamlar','Çamurluk ve Plastik Malzemeler'],
      image: '/42.png'
    },
    {
      title: 'Hırdavat & Cıvata',
      description: 'Montaj ve tamir işlemleri için gerekli tüm hırdavat malzemeleri ve cıvata çeşitleri. Kaliteli ve dayanıklı ürünler.',
      benefits: ['Silikon Sızdırmazlık Malzemeleri', 'Araç Bakım Spreyleri', 'Cıvata çeşitleri', 'Somun ve rondela', 'Vida setleri', 'Anahtar takımları', 'El aletleri', 'Montaj malzemeleri'],
      image: '/40.png'
    },
    {
      title: 'Aksesuar',
      description: 'Tır ve dorse araçlarınız için çeşitli aksesuar ürünleri. Güvenlik, konfor ve işlevsellik için gerekli tüm aksesuarlar.',
      benefits: ['Güvenlik aksesuarları', 'Konfor ürünleri', 'İşlevsel aksesuarlar', 'Dekoratif ürünler', 'Bakım aksesuarları', 'Özel ekipmanlar'],
      image: '/41.png'
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '60vh',
          background: 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 50%, #e2e8f0 100%)',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 80%, rgba(30, 58, 138, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(30, 58, 138, 0.1) 0%, transparent 50%)',
            zIndex: 0,
          }
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, md: 4, lg: 6 } }}>
          <Grid container spacing={{ xs: 4, md: 6, lg: 8 }} alignItems="center">
            <Grid item xs={12} lg={6}>
              <Fade in timeout={1000}>
                <Box>
                  <Button
                    component={RouterLink}
                    to="/hizmetler"
                    startIcon={<ArrowBack />}
                    sx={{ mb: 3, textTransform: 'none' }}
                  >
                    Hizmetlere Dön
                  </Button>
                  <Typography variant="h1" component="h1" gutterBottom sx={{ 
                    fontWeight: 700, 
                    color: 'text.primary',
                    fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' },
                    lineHeight: 1.2,
                    mb: 3
                  }}>
                    Tır ve Dorse Yedek Parça ve Aksesuarları -  Hırdavat ve Cıvata
                  </Typography>
                  <Typography variant="h6" paragraph sx={{ 
                    mb: 4, 
                    color: 'text.secondary',
                    lineHeight: 1.6,
                    fontSize: '1.1rem'
                  }}>
                    Bünyemizde bulunan Kale Tır Market Hırdavat ve Cıvata'dan Orijinal ve kaliteli yedek parçalarla araçlarını ve dorse kasalarını yeniden hayata döndürebilirsiniz.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Check sx={{ color: 'primary.main', fontSize: 20 }} />
                      <Typography variant="body1" color="text.secondary">
                        Orijinal Parçalar
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Check sx={{ color: 'primary.main', fontSize: 20 }} />
                      <Typography variant="body1" color="text.secondary">
                        Hızlı Teslimat
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Fade in timeout={1500}>
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      transition: 'transform 0.3s ease',
                    }
                  }}
                >
                  <LoadingImage
                    src="/23.png"
                    alt="Yedek Parça Hizmeti"
                    style={{
                      width: '100%',
                      height: '400px',
                      objectFit: 'cover',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                      p: 3,
                      color: 'white',
                    }}
                  >
                    <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Kaliteli Yedek Parçalar
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      Orijinal parçalarla güvenilir çözümler
                    </Typography>
                  </Box>
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Paper
        elevation={0}
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 12,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md" sx={{ px: { xs: 2, md: 4, lg: 6 } }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Kale Tır Market'e Göz Atın
          </Typography>
          <Typography variant="body1" paragraph sx={{ mb: 4, fontSize: '1.1rem' }}>
            Tüm ürün gruplarımızı ve fiyatlarımızı görmek için online mağazamızı ziyaret edin
          </Typography>
          <Button
            href="https://kaletirmarket.com"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            size="large"
            sx={{
              bgcolor: 'white',
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'grey.100',
              },
            }}
          >
            Kale Tır Market'e Git
          </Button>
        </Container>
      </Paper>

      {/* Features Section */}
      <Container maxWidth="xl" sx={{ py: 8, px: { xs: 2, md: 4, lg: 6 } }}>
        <Box textAlign="center" mb={5}>
          <Typography variant="h4" component="h2" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
            Ürün Gruplarımız
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto', fontSize: '1.1rem' }}>
            Geniş ürün yelpazemizle tüm ihtiyaçlarınızı karşılıyoruz
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 3, md: 4, lg: 5 }}>
          {productGroups.map((group, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Zoom in timeout={1000 + index * 200}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    p: 2.5,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <CardContent>
                    {/* Product Image */}
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                      <LoadingImage
                        src={group.image}
                        alt={group.title}
                        style={{
                          width: '120px',
                          height: '80px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                        }}
                      />
                    </Box>
                    
                    <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold', mb: 1.5, textAlign: 'center' }}>
                      {group.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.5, mb: 1.5, textAlign: 'center', fontSize: '0.9rem' }}>
                      {group.description}
                    </Typography>
                    <Grid container spacing={0.5}>
                      {group.benefits.map((benefit, benefitIndex) => (
                        <Grid item xs={6} key={benefitIndex}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.3 }}>
                            <Check color="primary" sx={{ fontSize: 14 }} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                              {benefit}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default YedekParca;
